import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { $apiAllo } from '@/services/api-allo/client';
import { type CompaniesSearchParams, CompaniesView, parseUrlFilters } from '@/views/companies';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/companies')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): CompaniesSearchParams => {
    return {
      filters: typeof search.filters === 'string' ? search.filters : undefined,
      sortField: typeof search.sortField === 'string' ? search.sortField : undefined,
      sortOrder:
        search.sortOrder === 'asc' || search.sortOrder === 'desc' ? search.sortOrder : undefined,
    };
  },
  onEnter: () => {
    analytics.track(ANALYTICS_EVENTS.COMPANIES.TAB_OPENED);
  },
  loaderDeps: ({ search }) => ({
    hasFilter: parseUrlFilters(search.filters).length > 0,
  }),
  loader: async ({ context, deps }) => {
    const { queryClient } = context as { queryClient: QueryClient };

    if (deps.hasFilter) {
      return;
    }

    const companiesQuery = $apiAllo.queryOptions('get', '/v2/company', {
      params: {
        query: {
          'page[size]': 50,
          'page[number]': 0,
          sort: 'activity',
        },
      },
    });

    await queryClient
      .prefetchInfiniteQuery({
        queryKey: companiesQuery.queryKey,
        queryFn: companiesQuery.queryFn,
        // @ts-expect-error FIXME: fix the api types
        initialPageParam: 0,
        staleTime: 30 * 60_000,
      })
      .catch((error) => {
        console.warn('Failed to prefetch companies:', error);
      });
  },
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const searchParams = Route.useSearch();

  const handleNavigate = (updater: (prev: CompaniesSearchParams) => CompaniesSearchParams) => {
    navigate({ search: updater });
  };

  return <CompaniesView searchParams={searchParams} onNavigate={handleNavigate} />;
}
